import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { api } from '../src/services/api';
import { logUsageEvent } from '../src/lib/logger';
import { MapaUnidadeData } from '../types';
import { RefreshCw, Map as MapIcon } from 'lucide-react';
import { useAuth } from '../src/contexts/AuthContext';

// Fix for default marker icons in standard Leaflet with webpack/vite
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Create custom cluster icon for that glowing effect
const createClusterCustomIcon = function (cluster: any) {
    return L.divIcon({
        html: `<div class="bg-primary/90 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold shadow-lg shadow-primary/40 text-sm border-2 border-white relative">
             ${cluster.getChildCount()}
             <span class="absolute inset-0 rounded-full bg-primary/30 animate-ping"></span>
           </div>`,
        className: 'custom-marker-cluster',
        iconSize: L.point(40, 40, true),
    });
};

const DashboardMap: React.FC = () => {
    const { profile } = useAuth();
    const [locData, setLocData] = useState<MapaUnidadeData[]>([]);
    const [loading, setLoading] = useState(true);
    const [geocoding, setGeocoding] = useState(false);

    const fetchMapData = async () => {
        try {
            setLoading(true);
            const data = await api.mapa.getDadosUnidades();
            setLocData(data);
        } catch (error) {
            console.error('Erro ao buscar dados do mapa:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMapData();
    }, []);

    const handleUpdateCoordinates = async () => {
        setGeocoding(true);
        try {
            await api.mapa.triggerGeocode();
            logUsageEvent({ userId: profile?.id, modulo: 'dashboard', funcionalidade: null, acao: 'atualizar_coordenadas_mapa', origem: 'acao' });
            await fetchMapData();
        } catch (error) {
            console.error('Erro ao atualizar coordenadas:', error);
        } finally {
            setGeocoding(false);
        }
    };

    const missingCoordinates = locData.filter(d => !d.latitude || !d.longitude).length;

    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 shadow-sm flex flex-col gap-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-xl">
                        <MapIcon size={22} />
                    </div>
                    <div>
                        <h2 className="text-lg font-poppins font-bold text-gray-900 dark:text-white">Mapa de Clientes</h2>
                        <p className="text-xs text-gray-500 dark:text-gray-400 shrink-0">
                            Distribuição geográfica das Unidades Consumidoras
                        </p>
                    </div>
                </div>

                {missingCoordinates > 0 && (
                    <button
                        onClick={handleUpdateCoordinates}
                        disabled={geocoding}
                        className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-sm font-semibold rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors disabled:opacity-50"
                    >
                        <RefreshCw size={14} className={geocoding ? 'animate-spin' : ''} />
                        {geocoding ? 'Atualizando...' : `Geolocalizar pendentes (${missingCoordinates})`}
                    </button>
                )}
            </div>

            <div className="h-[500px] w-full rounded-xl overflow-hidden shadow-inner border border-gray-200 dark:border-gray-700 relative z-0">
                {loading ? (
                    <div className="w-full h-full flex items-center justify-center bg-gray-50 dark:bg-gray-800">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                ) : (
                    <MapContainer
                        center={[-14.235, -51.925]}
                        zoom={4}
                        scrollWheelZoom={true}
                        className="h-full w-full"
                    >
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <MarkerClusterGroup
                            chunkedLoading
                            iconCreateFunction={createClusterCustomIcon}
                            showCoverageOnHover={false}
                            maxClusterRadius={50}
                        >
                            {locData.filter(d => d.latitude && d.longitude).map((city, cIdx) => {
                                const isAdminOrGerente = profile?.role === 'adm' || profile?.role === 'gerente';

                                if (isAdminOrGerente) {
                                    // Full view for Admins and Managers
                                    const unidades = city.unidades || Array.from({ length: city.total_ucs }).map((_, i) => ({
                                        numero_uc: `N/A`,
                                        outro_numero_uc: undefined,
                                        nome_cliente: 'Cliente Não Informado'
                                    }));

                                    return unidades.map((uc, i) => {
                                        const jitterLat = city.latitude! + (Math.random() - 0.5) * 0.005;
                                        const jitterLng = city.longitude! + (Math.random() - 0.5) * 0.005;

                                        return (
                                            <Marker
                                                key={`${city.cidade}-${cIdx}-${i}`}
                                                position={[jitterLat, jitterLng]}
                                            >
                                                <Popup>
                                                    <div className="font-semibold text-gray-900 border-b pb-1 mb-1">
                                                        {uc.nome_cliente || 'Cliente Sem Nome'}
                                                    </div>
                                                    <div className="text-sm text-gray-700">
                                                        <span className="font-medium">UC:</span> {uc.numero_uc || 'N/A'} {uc.outro_numero_uc && <span className="text-[10px] bg-gray-100 px-1 rounded font-bold text-gray-500">{uc.outro_numero_uc}</span>}
                                                    </div>
                                                    <div className="text-xs text-gray-500 mt-2">
                                                        {city.cidade} - {city.estado} ({i + 1} de {city.total_ucs})
                                                    </div>
                                                </Popup>
                                            </Marker>
                                        );
                                    });
                                } else {
                                    // Restricted view for other roles
                                    return Array.from({ length: city.total_ucs }).map((_, i) => {
                                        const jitterLat = city.latitude! + (Math.random() - 0.5) * 0.005;
                                        const jitterLng = city.longitude! + (Math.random() - 0.5) * 0.005;

                                        return (
                                            <Marker
                                                key={`${city.cidade}-${cIdx}-${i}-restricted`}
                                                position={[jitterLat, jitterLng]}
                                            >
                                                <Popup>
                                                    <div className="font-semibold text-gray-900">{city.cidade} - {city.estado}</div>
                                                    <div className="text-gray-600">Total na cidade: {city.total_ucs} UCs</div>
                                                    {city.total_ucs > 1 && <div className="text-xs text-gray-400 mt-1">Este marcador compõe 1 de {city.total_ucs} nesta cidade.</div>}
                                                </Popup>
                                            </Marker>
                                        );
                                    });
                                }
                            })}
                        </MarkerClusterGroup>
                    </MapContainer>
                )}
            </div>
        </div>
    );
};

export default DashboardMap;
